import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import UserAvatar from '../components/UserAvatar';
import { ListenerBadge } from '../components/ListenerBadge';
import { Link } from 'react-router-dom';
import {
  Trophy, Medal, Crown, ArrowLeft, Loader2, Flame,
  Headphones, Music, Users, Globe, Calendar,
} from 'lucide-react';

function RankBadge({ position }) {
  if (position === 0) return <Crown className="w-5 h-5 text-amber-400" />;
  if (position === 1) return <Medal className="w-5 h-5 text-foreground/80" />;
  if (position === 2) return <Medal className="w-5 h-5 text-amber-600" />;
  return <span className="text-sm font-bold text-muted-foreground/60 w-5 text-center">#{position + 1}</span>;
}

function PodiumCard({ entry, idx, isMe }) {
  const isFirst = idx === 0;
  return (
    <Card
      className={`p-3 text-center border ${isFirst ? 'bg-amber-500/10 border-amber-500/20' : 'bg-card/50 border-border'}`}
      style={{ order: idx === 1 ? 0 : idx === 0 ? 1 : 2 }}
      data-testid={`podium-${idx}`}
    >
      <div className="flex justify-center mb-2"><RankBadge position={idx} /></div>
      <UserAvatar src={entry.avatar_url} name={entry.name} size="sm" className={isFirst ? 'w-14 h-14 mx-auto' : 'w-10 h-10 mx-auto'} />
      <p className="text-sm font-bold mt-2 truncate">{entry.name} {isMe && <span className="text-violet-400">(You)</span>}</p>
      {entry.rank && <p className="text-xs text-muted-foreground">{entry.rank}</p>}
      {entry.tier_label && <ListenerBadge tier={entry.tier} size="xs" showLabel />}
      <p className="text-xs font-mono mt-1" style={{ color: entry.rank_color || entry.tier_color || '#9ca3af' }}>
        {(entry.total_xp || entry.weekly_xp || 0).toLocaleString()} XP
      </p>
    </Card>
  );
}

function LeaderboardRow({ entry, pos, isMe }) {
  return (
    <Card
      className={`flex items-center gap-3 p-3 border ${isMe ? 'bg-violet-600/10 border-violet-500/20' : 'bg-card/50 border-border'}`}
      data-testid={`leaderboard-row-${pos}`}
    >
      <RankBadge position={pos} />
      <UserAvatar src={entry.avatar_url} name={entry.name} size="sm" className="w-9 h-9" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {entry.name} {isMe && <span className="text-violet-400">(You)</span>}
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {entry.rank && <span style={{ color: entry.rank_color }}>{entry.rank}</span>}
          {entry.tier_label && <ListenerBadge tier={entry.tier} size="xs" />}
          {entry.level && <span>Lv.{entry.level}</span>}
          {entry.post_streak > 0 && (
            <span className="flex items-center gap-0.5 text-orange-400">
              <Flame className="w-3 h-3" />{entry.post_streak}
            </span>
          )}
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-mono font-bold" style={{ color: entry.rank_color || entry.tier_color || '#9ca3af' }}>
          {(entry.total_xp || entry.weekly_xp || 0).toLocaleString()}
        </p>
        <p className="text-[10px] text-muted-foreground">XP</p>
      </div>
    </Card>
  );
}

export default function LeaderboardPage() {
  const { getAuthHeaders, user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boardType, setBoardType] = useState('creators'); // creators | listeners
  const [scope, setScope] = useState('global');            // global | dmv | weekly

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const headers = {};
        try { Object.assign(headers, getAuthHeaders()); } catch {}

        let url;
        if (boardType === 'creators') {
          url = `${API}/gamification/leaderboard?scope=${scope}&limit=25`;
        } else {
          url = `${API}/listener/leaderboard?scope=${scope}&limit=25`;
        }

        const res = await fetch(url, { headers });
        if (res.ok) {
          const data = await res.json();
          setEntries(data.entries || []);
        }
      } catch {} finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, [boardType, scope, getAuthHeaders]);

  const boardTabs = [
    { id: 'creators', label: 'Creators', icon: Music },
    { id: 'listeners', label: 'Listeners', icon: Headphones },
  ];

  const scopeTabs = boardType === 'listeners'
    ? [
        { id: 'global', label: 'Global', icon: Globe },
        { id: 'dmv', label: 'DMV', icon: Users },
        { id: 'weekly', label: 'Weekly', icon: Calendar },
      ]
    : [
        { id: 'global', label: 'Global', icon: Globe },
        { id: 'dmv', label: 'DMV', icon: Users },
      ];

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="leaderboard-page">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/feed">
            <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Trophy className="w-6 h-6 text-amber-400" /> Leaderboard
            </h1>
            <p className="text-sm text-muted-foreground">
              {boardType === 'creators' ? 'Top creators by XP' : 'Top listeners by engagement'}
            </p>
          </div>
        </div>

        {/* Board Type Tabs */}
        <div className="flex gap-1 p-1 rounded-xl bg-muted/20 mb-4" data-testid="board-type-tabs">
          {boardTabs.map(t => (
            <button
              key={t.id}
              onClick={() => { setBoardType(t.id); setScope('global'); }}
              className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                boardType === t.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`board-${t.id}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Scope Tabs */}
        <div className="flex gap-2 mb-6">
          {scopeTabs.map(s => (
            <button
              key={s.id}
              onClick={() => setScope(s.id)}
              className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                scope === s.id ? 'bg-muted/50 text-foreground' : 'text-muted-foreground/60 hover:text-muted-foreground/80'
              }`}
              data-testid={`scope-${s.id}`}
            >
              <s.icon className="w-3.5 h-3.5" /> {s.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : entries.length === 0 ? (
          <Card className="p-8 text-center bg-card/50 border border-border">
            <Trophy className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              {boardType === 'listeners' ? 'No listener activity yet. Start engaging!' : 'No entries yet. Start earning XP!'}
            </p>
          </Card>
        ) : (
          <div className="space-y-2">
            {/* Podium for top 3 */}
            {entries.slice(0, 3).length >= 3 && (
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[1, 0, 2].map(idx => {
                  const e = entries[idx];
                  if (!e) return null;
                  return <PodiumCard key={e.user_id} entry={e} idx={idx} isMe={user?.user_id === e.user_id} />;
                })}
              </div>
            )}

            {/* Remaining rows */}
            {entries.slice(entries.length >= 3 ? 3 : 0).map((e, i) => {
              const pos = entries.length >= 3 ? i + 3 : i;
              return <LeaderboardRow key={e.user_id} entry={e} pos={pos} isMe={user?.user_id === e.user_id} />;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
